import { useEffect, useState } from 'react'
import { getPhotos, photoObjectURL } from '../data/photoStore.js'
import './photostrip.css'

/*
 * PhotoStrip — Assignment photos, shown wherever they are needed.
 *
 * Photo blobs live in IndexedDB (see photoStore.js); what travels around the
 * app is a list of ids. This resolves those ids to object URLs for display and
 * — importantly — revokes them on unmount. An object URL pins its blob in
 * memory until revoked, so a screen that opened several assignments would
 * otherwise hold every photo it had ever shown.
 *
 * `onRemove` makes it an editor; without it, it is read-only, which is what
 * the worker sees.
 */
export default function PhotoStrip({ photoIds = [], onRemove = null, emptyLabel = null }) {
  const [items, setItems] = useState([])
  const key = photoIds.join('|')

  useEffect(() => {
    let alive = true
    const created = []
    if (!photoIds.length) { setItems([]); return undefined }
    getPhotos(photoIds)
      .then((recs) => {
        if (!alive) return
        setItems(recs.map((r) => {
          const url = photoObjectURL(r)
          if (url) created.push(url)
          return { id: r.id, name: r.name, url }
        }))
      })
      .catch(() => { if (alive) setItems([]) })
    return () => {
      alive = false
      created.forEach((u) => { try { URL.revokeObjectURL(u) } catch { /* already gone */ } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  if (!photoIds.length) {
    return emptyLabel ? <p className="ps-empty">{emptyLabel}</p> : null
  }

  return (
    <ul className="ps" aria-label={`${photoIds.length} photo${photoIds.length === 1 ? '' : 's'}`}>
      {items.map((it) => (
        <li key={it.id} className="ps__item">
          {it.url
            ? <img className="ps__img" src={it.url} alt={it.name || 'Assignment photo'} />
            : <span className="ps__missing">image unavailable</span>}
          {onRemove && (
            <button type="button" className="ps__remove" aria-label={`Remove ${it.name || 'photo'}`}
                    onClick={() => onRemove(it.id)}>×</button>
          )}
        </li>
      ))}
    </ul>
  )
}
